/* istanbul ignore file */ /* plugin extension code */
import { Platform } from 'react-native';
import type { LDUser } from 'launchdarkly-js-client-sdk';
import { initialize } from 'launchdarkly-js-client-sdk';
import { logger as LOG } from '@sg/garnish';
import '@expo/browser-polyfill';

import { getAppVersion, getEnvVars } from '@order/utils';

// ────────────────────────────────────────────────────────────────────────────────
// LOG.enable('LAUNCHDARKLY');
const logger = LOG.extend('LAUNCHDARKLY');
// ────────────────────────────────────────────────────────────────────────────────

const { LAUNCHDARKLY_CLIENT_ID } = getEnvVars();

export const initLaunchDarkly = async (userInfo?: LDUser) => {
  if (!LAUNCHDARKLY_CLIENT_ID) {
    logger.warn('LAUNCHDARKLY_CLIENT_ID is missing');

    return;
  }

  locationHashPolyfill();

  const client = initialize(
    LAUNCHDARKLY_CLIENT_ID,
    getLaunchDarklyUser(userInfo),
  );

  await client.waitForInitialization();

  logger.debug('LaunchDarkly client initialized');

  return client;
};

// ────────────────────────────────────────────────────────────────────────────────

/**
 * Small polyfill for adding 'hash' property to window.location.
 *
 * location.hash is undefined in case of native platforms,
 * which causes launch darkly client to throw an error.
 */
function locationHashPolyfill() {
  if (typeof window.location.hash === 'undefined') {
    // eslint-disable-next-line functional/immutable-data
    window.location.hash = '';
  }
}

export function getLaunchDarklyUser(userInfo?: LDUser): LDUser {
  const baseUser = userInfo ?? { anonymous: true };

  return { ...baseUser, ...getLaunchDarklyMetadata() };
}

/**
 * Adds metadata to the LaunchDarkly user, allowing more complex targeting rules.
 * @see: https://docs.launchdarkly.com/guides/sdk/mobile#targeting-the-app-version-number-os-and-device-when-enabling-new-features
 */
function getLaunchDarklyMetadata(): LDUser {
  return {
    custom: {
      platform: Platform.OS,
      appVersion: getAppVersion(),
    },
  };
}
